import type { AgentFindingApprovalStatus } from '../data-runtime'

export interface ReviewActionsProps {
  resultId?: string
  /** Stable finding identity for batch reject. Defaults to resultId. */
  itemId?: string
  resultIds?: string[]
  mode?: 'item' | 'resolve'
  status?: AgentFindingApprovalStatus
  resultType?: string
  reviewableTypes?: string[] | null
  runId?: string
}

export type ReviewActionsTarget =
  | { kind: 'item'; id: string }
  | { kind: 'resolve'; ids: string[] }
  | { kind: 'misuse'; message: string }

/**
 * Fail-open: missing schema info means reviewable. Hide only when the
 * type is listed as present on the schema and is not in the reviewable set.
 */
export function isReviewableFindingType(
  resultType: string | undefined,
  reviewableTypes: string[] | null | undefined
): boolean {
  if (reviewableTypes == null || resultType == null || resultType === '') return true
  return reviewableTypes.includes(resultType)
}

export function resolveReviewActionsTarget(props: {
  resultId?: string
  resultIds?: string[]
  mode?: 'item' | 'resolve'
}): ReviewActionsTarget {
  const id = typeof props.resultId === 'string' ? props.resultId.trim() : ''
  const ids = Array.isArray(props.resultIds)
    ? props.resultIds
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter(Boolean)
    : []
  const hasId = id !== ''
  const hasIds = ids.length > 0

  if (hasId && hasIds) {
    return {
      kind: 'misuse',
      message:
        'ReviewActions: pass resultId (one finding) or resultIds (resolve), not both. Bind ids from useAgentFindings — useAgentResults documents have none.'
    }
  }
  if (!hasId && !hasIds) {
    return {
      kind: 'misuse',
      message:
        'ReviewActions: needs resultId or resultIds from useAgentFindings. Document rows from useAgentResults have no id to patch.'
    }
  }
  const mode = props.mode ?? (hasIds ? 'resolve' : 'item')
  if (mode === 'resolve') {
    return { kind: 'resolve', ids: hasIds ? ids : [id] }
  }
  if (hasIds) {
    return {
      kind: 'misuse',
      message: 'ReviewActions: mode="item" needs resultId, not resultIds.'
    }
  }
  return { kind: 'item', id }
}
